import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UploadCloud, FolderOpen } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { transcriptionApi, uploadToSignedUrl, orchestrateUpload } from '../utils/api';
import { transcodeTo16kMonoMp3, canTranscodeWithinMemory } from '../utils/audioTranscode';
import { useTrpgSystems } from '../hooks/queries/useTrpgSystems';
import { formatUsageHours, formatFileSize } from '../utils/formatters';
import { linkButtonClass } from '../utils/linkButtonClass';
import { Card, Button, Combobox, Textarea, Input } from '../components/ui';
import type { ComboboxItem } from '../components/ui';
import Layout from '../components/Layout';
import Header from '../components/Header';
import TermRowsInput from '../components/TermRowsInput';
import { buildTermsString, createTermRowItem } from '../utils/termRows';
import type { TermRowItem } from '../utils/termRows';
import type { User } from '../types';

interface UploadPageProps {
  user: User;
}

export default function UploadPage({ user }: UploadPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [sessionLog, setSessionLog] = useState('');
  const [trpgSystem, setTrpgSystem] = useState('');
  const [termRows, setTermRows] = useState<TermRowItem[]>(() => [createTermRowItem()]);
  const [numSpeakers, setNumSpeakers] = useState('');
  const { data: trpgSystems = [] } = useTrpgSystems();
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  // 進捗フェーズ: 送信前に 16kHz モノ MP3 へ変換（transcoding）→ 直アップロード（uploading）
  const [phase, setPhase] = useState<'transcoding' | 'uploading'>('uploading');
  const [error, setError] = useState('');
  // ドラッグ&ドロップの視覚フィードバック用。ドロップ領域上にファイルがある間だけ true。
  const [isDragging, setIsDragging] = useState(false);

  // クライアント側アップロード上限（サーバ MAX_UPLOAD_SIZE = 2GB に整合）
  const MAX_UPLOAD_SIZE = 2 * 1024 * 1024 * 1024;
  const MAX_UPLOAD_SIZE_LABEL = '2GB';

  // 受理する拡張子。まずブラウザで 16kHz mono MP3 へ変換して送る（アップロード軽量化）。
  // 変換に失敗した場合（コーデック非対応の動画・大容量など）は元ファイルをそのまま送信し、
  // サーバ側の ffmpeg 前処理が音声トラックを抽出する（動画も同経路で受理される）。
  const AUDIO_FORMATS = ['mp3', 'wav', 'm4a', 'flac'];
  const VIDEO_FORMATS = ['mp4', 'mov', 'm4v', 'webm'];
  const ACCEPTED_FORMATS = [...AUDIO_FORMATS, ...VIDEO_FORMATS];

  // 話者数入力は standard/unlimited のみ表示（UX上の出し分け。アクセス制御ではなく、実行可否はサーバ側ゲートが担保）
  const canSetSpeakers = user.plan.plan_type === 'standard' || user.plan.plan_type === 'unlimited';
  const SPEAKER_MIN = 1;
  const SPEAKER_MAX = 8;

  // ファイル選択・ドロップの共通検証（形式→サイズ）。合格時のみ audioFile を確定する。
  const validateAndSetFile = (file: File) => {
    // ファイル形式チェック
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !ACCEPTED_FORMATS.includes(ext)) {
      setError(`対応していない形式です。対応形式: ${ACCEPTED_FORMATS.join(', ')}`);
      setAudioFile(null);
      return;
    }

    // ファイルサイズチェック（サーバ/バケット上限 2GB に整合）
    if (file.size > MAX_UPLOAD_SIZE) {
      setError(`ファイルサイズが大きすぎます（最大: ${MAX_UPLOAD_SIZE_LABEL}）`);
      setAudioFile(null);
      return;
    }

    setError('');
    setAudioFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndSetFile(file);
  };

  // ドラッグ&ドロップ: ドロップ領域で既定のブラウザ挙動（ファイルを開く）を抑止し、選択と同じ検証に流す。
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!uploading) setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSetFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!audioFile) {
      setError('音声ファイルを選択してください');
      return;
    }

    // 話者数（任意）: 入力がある場合のみ 1〜8 の整数を検証。範囲外/非整数はここで弾く。
    // 未入力なら undefined を渡し、サーバ側で自動推定。
    let parsedNumSpeakers: number | undefined;
    if (canSetSpeakers && numSpeakers.trim() !== '') {
      const n = Number(numSpeakers);
      if (!Number.isInteger(n) || n < SPEAKER_MIN || n > SPEAKER_MAX) {
        setError(`話者数は${SPEAKER_MIN}〜${SPEAKER_MAX}の整数で入力してください`);
        return;
      }
      parsedNumSpeakers = n;
    }

    setUploading(true);
    setUploadPercent(0);
    setPhase('uploading');
    setError('');

    try {
      // 送信前変換: 16kHz モノ MP3 へ落として送信バイトを大幅削減（書き起こし品質は無劣化）。
      // ただし変換は音声長に比例したメモリを要求し、長時間ファイルではブラウザが Out of Memory で
      // 落ちて下の catch すら通らない。よって予算内に収まるファイルだけ変換する。
      // 変換しない場合・decode 不可（コーデック非対応の動画など）で失敗した場合は元ファイルを
      // そのまま送信し、サーバ側 ffmpeg 前処理が音声を抽出する（動画も同経路で受理される）。
      let fileToUpload: File = audioFile;
      if (await canTranscodeWithinMemory(audioFile)) {
        setPhase('transcoding');
        try {
          fileToUpload = await transcodeTo16kMonoMp3(audioFile, setUploadPercent);
        } catch (convErr: any) {
          console.warn('Audio transcode failed, uploading original:', convErr?.message ?? 'unknown error');
          fileToUpload = audioFile;
        }
      } else {
        console.info('Audio transcode skipped (memory budget), uploading original');
      }

      // ①署名URL取得 → ②Supabase直アップロード（進捗）→ ③storage_keyでジョブ作成。
      // 各段の失敗で後続を中断する（orchestrateUpload が保証）。UI 副作用はここで担う。
      setPhase('uploading');
      setUploadPercent(0);
      const result = await orchestrateUpload(
        {
          getUploadUrl: transcriptionApi.getUploadUrl,
          uploadToSignedUrl,
          create: transcriptionApi.create,
        },
        fileToUpload,
        {
          sessionLog: sessionLog || undefined,
          trpgSystem: trpgSystem || undefined,
          terms: buildTermsString(termRows),
          numSpeakers: parsedNumSpeakers,
        },
        setUploadPercent,
      );
      // 一覧キャッシュを無効化し、ダッシュボードに即時反映させる（staleTime による取りこぼし防止）
      queryClient.invalidateQueries({ queryKey: ['transcriptions'] });
      // アップロード成功 - 書き起こしページに遷移（ローディング画面表示）
      navigate(`/transcription/${result.id}`);
    } catch (err: any) {
      // ①③は axios エラー（response.data.detail）、②は素の Error（message）。
      // 形差を吸収し、秘匿情報（署名URL/トークン）を含まないメッセージへ正規化する。
      console.error('Upload error:', err?.message ?? 'unknown error');
      const errorMessage = err.response?.data?.detail || err.message || 'アップロードに失敗しました';
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // 使用時間の表記はダッシュボード・プロフィールと共通（formatUsageHours）。
  // 上限は購入した追加時間を含む。-1（無制限センチネル）はそのまま渡し、表記は共通関数に委ねる
  const hoursLimit =
    user.plan.hours_limit === -1 ? -1 : user.plan.hours_limit + (user.plan.extra_hours ?? 0);
  // 無制限プランの残りは「無制限」（引き算した数値には意味が無い）
  const hoursRemaining = hoursLimit === -1 ? -1 : Math.max(0, hoursLimit - user.plan.hours_used);

  // ファイル検証由来のエラー（形式/サイズ/未選択）は audioFile が null。送信失敗エラーは audioFile が選択済み。
  // file input の aria-invalid は前者のみに付与し、原因とフィールドの対応を一意に保つ（Login と同方針）。
  const isFileError = !!error && !audioFile;

  // 絞り込みは表示名に加えて読み（サーバ提供）と id も対象にする。
  // 「獣ノ森」を「けもの」で、「クトゥルフ神話TRPG（6版）」を「coc」で引けるようにするため。
  const systemItems: ComboboxItem[] = trpgSystems.map((system) => ({
    value: system.id,
    label: system.name,
    keywords: [system.reading ?? '', system.id],
  }));

  return (
    <Layout>
      <Header user={user} />

      <main className="max-w-3xl mx-auto px-4 py-8">
        <Card>
          <h2 className="text-2xl font-bold mb-2">音声アップロード</h2>
          <p className="text-sm text-gray-600 mb-6">
            日本語の音声のみ対応しています（Japanese audio only）
          </p>

          <form onSubmit={handleSubmit}>
            {/* プラン使用状況 */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-2">今月の使用状況</h3>
              <div className="text-sm">
                <span className="text-gray-600">残り時間:</span>
                <span className="ml-2 font-bold text-primary">
                  {formatUsageHours(hoursRemaining)}
                </span>
                <span className="text-gray-500 ml-2">
                  ({formatUsageHours(user.plan.hours_used)} / {formatUsageHours(hoursLimit)} 使用済み)
                </span>
              </div>
            </div>

            {/* ファイル選択（ドラッグ&ドロップ対応。中央のボタンからファイル選択も可能） */}
            <div className="mb-6">
              <label htmlFor="upload-file" className="block text-sm font-medium mb-2">
                音声・動画ファイル <span className="text-danger" aria-hidden="true">*</span>
                <span className="sr-only">（必須）</span>
              </label>
              <div
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed px-6 py-12 text-center transition-colors ${
                  isDragging
                    ? 'border-primary bg-primary/10'
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100/70'
                } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <UploadCloud
                  className={`h-14 w-14 ${isDragging ? 'text-primary' : 'text-primary/60'}`}
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
                <div>
                  <p className="text-base font-semibold text-text">
                    {isDragging ? 'ここにドロップして追加' : 'ここにファイルをドラッグ&ドロップ'}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">または</p>
                </div>
                {/* 中央のかわいい選択ボタン。実体の file input は視覚的に隠し label で発火する */}
                <label
                  htmlFor="upload-file"
                  className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-hover focus-within:outline-none focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2"
                >
                  <FolderOpen className="h-4 w-4" aria-hidden="true" />
                  ファイルを選択
                </label>
                <input
                  id="upload-file"
                  type="file"
                  accept=".mp3,.wav,.m4a,.flac,.mp4,.mov,.m4v,.webm,audio/*,video/*"
                  onChange={handleFileChange}
                  aria-invalid={isFileError || undefined}
                  aria-describedby={isFileError ? 'upload-error' : 'upload-file-hint'}
                  className="sr-only"
                  disabled={uploading}
                />
              </div>

              {/* 選択済みファイル */}
              {audioFile && (
                <p className="mt-3 text-sm text-gray-700">
                  ✓ 選択済み: {audioFile.name}（{formatFileSize(audioFile.size)}）
                </p>
              )}

              {/* ファイル検証エラー（形式/サイズ/未選択）はドロップ範囲の直下に表示 */}
              {isFileError && (
                <p id="upload-error" role="alert" className="mt-2 text-sm font-medium text-danger-fg">
                  {error}
                </p>
              )}

              <p id="upload-file-hint" className="mt-2 text-xs text-gray-500">
                対応形式: 音声 MP3・WAV・M4A・FLAC / 動画 MP4・MOV・M4V・WEBM（最大 {MAX_UPLOAD_SIZE_LABEL}）。動画は音声を自動で抽出します。
              </p>
            </div>

            {/* TRPGシステム選択 */}
            <div className="mb-6">
              <label htmlFor="upload-system" className="block text-sm font-medium mb-2">
                TRPGシステム（任意）
              </label>
              <Combobox
                id="upload-system"
                value={trpgSystem}
                onChange={setTrpgSystem}
                items={systemItems}
                placeholder="システム名で検索または選択"
                aria-describedby="upload-system-hint"
                disabled={uploading}
              />
              <p id="upload-system-hint" className="mt-2 text-xs text-gray-500">
                システム名の一部を入力すると絞り込めます（かな入力も可）。選択すると専用用語辞書を使用して認識精度が向上します
              </p>
            </div>

            {/* 話者数（standard/unlimited のみ表示・任意） */}
            {canSetSpeakers && (
              <div className="mb-6">
                <label htmlFor="upload-speakers" className="block text-sm font-medium mb-2">
                  話者数（任意）
                </label>
                <Input
                  id="upload-speakers"
                  type="number"
                  min={SPEAKER_MIN}
                  max={SPEAKER_MAX}
                  step={1}
                  value={numSpeakers}
                  onChange={(e) => setNumSpeakers(e.target.value)}
                  placeholder={`${SPEAKER_MIN}〜${SPEAKER_MAX}`}
                  aria-describedby="upload-speakers-hint"
                  disabled={uploading}
                />
                <p id="upload-speakers-hint" className="mt-2 text-xs text-gray-500">
                  参加人数が分かる場合に入力すると、話者分離の精度が上がります（{SPEAKER_MIN}〜{SPEAKER_MAX}名）。未入力なら自動推定します。
                </p>
              </div>
            )}

            {/* セッションログ */}
            <div className="mb-6">
              <label htmlFor="upload-log" className="block text-sm font-medium mb-2">
                セッションログ（任意）
              </label>
              <Textarea
                id="upload-log"
                value={sessionLog}
                onChange={(e) => setSessionLog(e.target.value)}
                placeholder="セッションのメモや参加者情報など..."
                aria-describedby="upload-log-hint"
                rows={4}
                disabled={uploading}
              />
              <p id="upload-log-hint" className="mt-2 text-xs text-gray-500">
                書き起こしと合わせて表示されるメモを入力できます
              </p>
            </div>

            {/* 卓の固有名詞（任意）: 用語・読み仮名の行入力 */}
            <TermRowsInput rows={termRows} onChange={setTermRows} disabled={uploading} />

            {/* アップロード進捗（②直アップロード中のバイト進捗。0→100 単調・完了で100%） */}
            {uploading && (
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">{phase === 'transcoding' ? '音声を変換中...' : 'アップロード中...'}</span>
                  <span className="font-medium text-primary">{uploadPercent}%</span>
                </div>
                <div
                  role="progressbar"
                  aria-valuenow={uploadPercent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="アップロード進捗"
                  className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden"
                >
                  <div
                    className="h-full bg-primary transition-[width] duration-150"
                    style={{ width: `${uploadPercent}%` }}
                  />
                </div>
              </div>
            )}

            {/* 送信失敗エラー（ファイル検証エラーはドロップ範囲の直下に表示済み） */}
            {error && !isFileError && (
              <div role="alert" className="mb-6 p-4 bg-danger-bg border border-danger/30 rounded-field text-danger-fg">
                {error}
              </div>
            )}

            {/* アップロードボタン */}
            <div className="flex gap-4">
              <Button
                type="submit"
                loading={uploading}
                disabled={!audioFile || uploading}
                size="sm"
                className="flex-1 whitespace-nowrap sm:px-8 sm:py-2.5 sm:text-base"
              >
                {uploading ? (phase === 'transcoding' ? '音声を変換中...' : 'アップロード中...') : 'アップロード開始'}
              </Button>
              <Link
                to="/dashboard"
                className={linkButtonClass('secondary', 'flex-1 whitespace-nowrap sm:px-8 sm:py-2.5 sm:text-base', 'sm')}
              >
                キャンセル
              </Link>
            </div>
          </form>

          {/* 注意事項 */}
          <div className="mt-8 p-4 bg-background border border-primary/20 rounded-lg">
            <h3 className="font-semibold text-text-heading mb-2">📝 注意事項</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• アップロードした音声は自動的に書き起こされます</li>
              <li>• 処理時間は音声の長さによって異なります</li>
              <li>• 書き起こし結果は完了から8時間後に自動削除されます（無制限プランは7日間保持）</li>
              <li>• 無制限プランのみ、結果画面で各セグメントの音声を保持期限まで再生できます（本人のみ・期限到来で音声も削除）</li>
              <li>• プライバシー保護のため、定期的にダウンロードしてください</li>
            </ul>
          </div>
        </Card>
      </main>
    </Layout>
  );
}
